import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import type { Company } from '@/interfaces/company'
import { loadLocaleMessages, isLocaleLoaded } from '@/translations/loader'
import type { I18n } from 'vue-i18n'

// Get i18n instance from the global app
let i18nInstance: I18n | null = null

export interface BreadcrumbItem {
  label: string
  /** Nome di rotta: se presente la voce è un link. */
  route?: string
}

export const useAppStore = defineStore('app', () => {
  const currentCompany: Ref<Company | null> = ref(null)
  const currentLocale: Ref<string> = ref(localStorage.getItem('locale') || 'it')

  const selectCompany = (company: Company) => {
    currentCompany.value = company
  }

  // Vero solo dopo la risposta di user/me/companies (successo o fallimento): evita di
  // mostrare per un istante lo stato "nessuna azienda" mentre la lista sta ancora caricando.
  const companiesLoaded = ref(false)
  const setCompaniesLoaded = (loaded: boolean) => {
    companiesLoaded.value = loaded
  }

  const breadcrumbs: Ref<BreadcrumbItem[]> = ref([])
  const setBreadcrumbs = (items: BreadcrumbItem[]) => {
    breadcrumbs.value = items
  }

  // Chi vuole aprire lo switcher (es. lo stato senza azienda) incrementa il contatore;
  // CompanySwitcher lo osserva e apre il popover.
  const companySwitcherRequest = ref(0)
  const requestCompanySwitcher = () => {
    companySwitcherRequest.value++
  }

  // Set the i18n instance (called from main.ts after i18n is created)
  const setI18n = (i18n: I18n) => {
    i18nInstance = i18n
  }

  // Async function to change locale with lazy loading
  async function setLocale(locale: string) {
    if (!i18nInstance) {
      console.warn('i18n instance not set in app store')
      return
    }

    if (!isLocaleLoaded(i18nInstance, locale)) {
      await loadLocaleMessages(i18nInstance, locale)
    }

    currentLocale.value = locale
    ;(i18nInstance.global.locale as any).value = locale
    localStorage.setItem('locale', locale)
    document.documentElement.lang = locale
  }

  return {
    currentCompany,
    selectCompany,
    companiesLoaded,
    setCompaniesLoaded,
    breadcrumbs,
    setBreadcrumbs,
    companySwitcherRequest,
    requestCompanySwitcher,
    currentLocale,
    setLocale,
    setI18n
  }
})
